import React from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'

/**
 * Toggle switch (atom).
 */
const Toggle = React.forwardRef(({
  active = false,
  disabled = false,
  loading = false,
  onToggle = () => {},
  className,
  activeColor,
  inactiveColor,
}, ref) => {
  const handleClick = e => {
    e.stopPropagation()
    if (disabled || loading) return
    onToggle(!active)
  }

  const resolvedActiveColor = activeColor ?? 'var(--color-brand-04)'
  const resolvedInactiveBorder = inactiveColor ?? 'var(--color-Neutral-3)'
  // If the caller passes an inactive color, fill the track with it too — this
  // turns the toggle into a two-tone segmented control. Without a custom
  // inactiveColor we keep the default neutral surface so existing on/off
  // toggles look unchanged.
  const resolvedInactiveBackground = inactiveColor ?? 'var(--color-surface-1)'
  const inactiveThumbBg = inactiveColor ? 'var(--color-surface-1)' : 'var(--color-Neutral-3)'

  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={active}
      disabled={disabled || loading}
      data-testid="toggle-control"
      data-checked={active}
      onClick={handleClick}
      className={classNames(
        'relative h-6 w-10 shrink-0 rounded-[var(--radius-circular)] border border-solid p-0 transition-colors duration-100 outline-offset-2 focus-visible:outline-[var(--color-brand-04)]',
        {
          'cursor-pointer hover:!border-[var(--color-Neutral-6)]': !disabled && !loading,
          'cursor-not-allowed opacity-50': disabled,
        },
        className,
      )}
      style={
        active
          ? {
              borderColor: resolvedActiveColor,
              backgroundColor: resolvedActiveColor,
            }
          : {
              borderColor: resolvedInactiveBorder,
              backgroundColor: resolvedInactiveBackground,
            }
      }>
      <motion.span
        className={classNames(
          'absolute top-[3px] left-[3px] block h-4 w-4 rounded-full',
          { hidden: loading },
        )}
        style={{ backgroundColor: active ? 'var(--color-surface-1)' : inactiveThumbBg }}
        animate={{ x: active ? 14 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        aria-hidden
      />
    </button>
  )
})

Toggle.displayName = 'Toggle'

export default Toggle
